import React, { useState } from "react";
import { Paper, Box, Typography, Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash";

import { getRuta } from "../../../helpers/Helpers";
import BackButton from "../../../components/ui/BackButton";
import SegmentedTabs from "../../../components/ui/SegmentedTabs";
import StatusChip from "../../../components/ui/StatusChip";
import CreditDetails from "./CreditDetails";
import CreditGarantias from "./CreditGarantias";
import CreditPayment from "./CreditPayment";
import CreditAbono from "./CreditAbono";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index ? <Box sx={{ marginTop: "18px" }}>{children}</Box> : null}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const iniciales = (nombre) => {
  const partes = (nombre || "").trim().split(/\s+/);
  return `${(partes[0] || "")[0] || ""}${(partes[1] || "")[0] || ""}`.toUpperCase();
};

const money = (n) =>
  new Intl.NumberFormat("es-NI", { style: "currency", currency: "NIO" }).format(n || 0);

// Detalle de crédito — estructura tomada de la rama CrediHouse_Formats:
// Regresar suelto arriba, tarjeta de resumen (avatar, estado, monto y cuota),
// pestañas, y el contenido debajo.
const CreditDetailsContainer = () => {
  let ruta = getRuta();
  let navigate = useNavigate();
  const { id } = useParams();
  const [value, setValue] = useState(0);
  const [clientName, setClientName] = useState("");
  const [dataCredit, setDataCredit] = useState("");

  // El estado se deriva de los campos que SI existen en esta rama. No hay
  // `isDenegado` (eso es de CrediHouse_Formats, que tiene modulo de denegados).
  const estadoBadge = () => {
    if (isEmpty(dataCredit)) {
      return null;
    }
    if (dataCredit.isCanceled) {
      return { label: "Cancelado", tone: "neutral" };
    }
    if (!dataCredit.isEntregado) {
      return { label: "Pendiente de entrega", tone: "warning" };
    }
    return { label: "Activo · Entregado", tone: "success" };
  };

  const badge = estadoBadge();

  return (
    <div>
      <Box sx={{ marginBottom: "18px" }}>
        <BackButton onClick={() => navigate(`${ruta}/credits`)} />
      </Box>

      <Paper
        sx={{
          padding: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "24px",
          flexWrap: "wrap",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "14px",
              backgroundColor: "rgba(18,163,90,.13)",
              color: "#12a35a",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Poppins', sans-serif",
              fontWeight: 600,
              fontSize: 19,
              flexShrink: 0,
            }}
          >
            {iniciales(clientName)}
          </Box>

          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 600,
                  fontSize: 20,
                  color: "text.primary",
                }}
              >
                {clientName}
              </Typography>
              {badge ? <StatusChip label={badge.label} tone={badge.tone} /> : null}
            </Stack>

            <Typography sx={{ fontSize: 13.5, color: "text.disabled", marginTop: "5px" }}>
              Crédito #{id}
              {!isEmpty(dataCredit) && dataCredit.destino ? ` · ${dataCredit.destino}` : ""}
            </Typography>
          </Box>
        </Stack>

        {!isEmpty(dataCredit) ? (
          <Stack direction="row" spacing={4.5} flexWrap="wrap" useFlexGap>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: "text.disabled", fontWeight: 700 }}>
                Monto financiado
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 22,
                  color: "text.primary",
                  marginTop: "4px",
                }}
              >
                {money(dataCredit.montoPrincipal)}
              </Typography>
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12.5, color: "text.disabled", fontWeight: 700 }}>
                Cuota
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: 700,
                  fontSize: 22,
                  color: "text.primary",
                  marginTop: "4px",
                }}
              >
                {money(dataCredit.cuotaPagar)}
              </Typography>
            </Box>
          </Stack>
        ) : null}
      </Paper>

      <Box sx={{ marginTop: "20px" }}>
        <SegmentedTabs
          tabs={[
            { label: "Crédito" },
            { label: "Garantías" },
            { label: "Plan de pagos" },
            { label: "Abonos" },
          ]}
          value={value}
          onChange={setValue}
        />
      </Box>

      <TabPanel value={value} index={0}>
        <CreditDetails
          id={id}
          setClientName={setClientName}
          setDataCredit={setDataCredit}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <CreditGarantias id={id} />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <CreditPayment
          id={id}
          clientName={clientName}
          dataCredit={dataCredit}
        />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <CreditAbono id={id} />
      </TabPanel>
    </div>
  );
};

export default CreditDetailsContainer;
